import BigNumber from 'bignumber.js'

/**
 * Sorts a copy of a list by its `order` field.
 *
 * Ordering is a reader's job here rather than the cache's. An ordering broadcast
 * -- `workspace_order_updated`, `applications_reordered` -- renumbers `order` on
 * every item and leaves the array in whatever sequence it arrived in, so a list
 * rendered straight from the cache is correct in its numbers and wrong on screen.
 *
 * The tie-break on id is not decoration. Those same broadcasts assign `0` to
 * every item they do not mention, so several items sharing an `order` is the
 * normal state right after one arrives, and without a second key the sidebar
 * would settle into whichever sequence the last patch happened to leave behind.
 */
export function byOrder<T extends { id: number; order: number }>(
  items: readonly T[],
): T[] {
  return [...items].sort((a, b) => a.order - b.order || a.id - b.id)
}

/**
 * The same, for the things whose `order` is an arbitrary-precision decimal.
 *
 * Rows, widgets and dashboard data sources are ordered by a value that grows
 * digits rather than being renumbered, so one dropped between two others gets an
 * order between theirs. That is why it arrives as a string, and why reading it
 * with `parseFloat` loses exactly the digits that separate the two neighbours.
 */
export function byDecimalOrder<T extends { id: number; order: string }>(
  items: readonly T[],
): T[] {
  return [...items].sort((a, b) => {
    const left = new BigNumber(a.order)
    const right = new BigNumber(b.order)
    if (left.isEqualTo(right)) return a.id - b.id
    return left.isLessThan(right) ? -1 : 1
  })
}
